import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { apiErrorMessage, createMachine } from '@/services/api'
import {
  EDITIONS,
  SERIAL_PATTERN,
  deviceNameFromSerial,
  suggestNextSerial,
  type ComponentDef,
  type Machine,
} from '@/types/machine'

interface RegisterMachineDialogProps {
  open: boolean
  machines: Machine[]
  // 部品マスタ。新規機材の構成部品の初期値になる
  componentDefs: ComponentDef[]
  // 台帳未登録の IoT デバイスを取り込む場合の初期値
  prefill?: { serialNumber?: string; deviceName?: string } | null
  onClose: () => void
  onCreated: (machine: Machine) => void
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

export default function RegisterMachineDialog({
  open,
  machines,
  componentDefs,
  prefill,
  onClose,
  onCreated,
}: RegisterMachineDialogProps) {
  const { t } = useTranslation()

  const [serialNumber, setSerialNumber] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [deviceNameTouched, setDeviceNameTouched] = useState(false)
  const [lot, setLot] = useState('')
  const [edition, setEdition] = useState('')
  const [customer, setCustomer] = useState('')
  const [bodySerial, setBodySerial] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const serial =
      prefill?.serialNumber ??
      suggestNextSerial(
        machines
          .map((m) => m.serialNumber)
          .filter((s): s is string => Boolean(s))
      )
    setSerialNumber(serial)
    setDeviceName(prefill?.deviceName ?? deviceNameFromSerial(serial))
    setDeviceNameTouched(Boolean(prefill?.deviceName))
    setLot(`${serial.slice(0, 4)}生産`)
    setEdition('')
    setCustomer('')
    setBodySerial('')
    setError(null)
  }, [open, prefill, machines])

  const mutation = useMutation({
    mutationFn: () =>
      createMachine({
        serialNumber,
        deviceName,
        lot,
        edition,
        customer,
        bodySerial,
        // 部品マスタを初期テンプレートとして構成部品を作る
        components: componentDefs.map(({ name, category }) => ({
          name,
          category,
        })),
      }),
    onSuccess: (machine) => onCreated(machine),
    onError: (err) => setError(apiErrorMessage(err) ?? t('register.error')),
  })

  if (!open) return null

  // シリアルは任意（未採番の車体・シミュレータ等）。入力時のみ形式チェック
  const serialValid = serialNumber === '' || SERIAL_PATTERN.test(serialNumber)
  // 完全に空の登録は防ぐ（何かしら識別子が必要）
  const hasIdentifier = Boolean(
    serialNumber || deviceName || bodySerial || customer
  )

  const handleSerialChange = (value: string) => {
    setSerialNumber(value)
    if (!deviceNameTouched) setDeviceName(deviceNameFromSerial(value))
    setLot(/^\d{4}/.test(value) ? `${value.slice(0, 4)}生産` : lot)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('register.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('machines.serial')}
            </label>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => handleSerialChange(e.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('register.serialHint')}
            </p>
            {serialNumber && !serialValid && (
              <p className="mt-1 text-xs text-red-600">
                {t('register.serialError')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.deviceName')}
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => {
                setDeviceName(e.target.value)
                setDeviceNameTouched(true)
              }}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('register.deviceNameHint')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('detail.lot')}
              </label>
              <input
                type="text"
                value={lot}
                onChange={(e) => setLot(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('machines.edition')}
              </label>
              <select
                value={edition}
                onChange={(e) => setEdition(e.target.value)}
                className={inputClass}
              >
                <option value="">-</option>
                {EDITIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('machines.customer')}
            </label>
            <input
              type="text"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('machines.bodySerial')}
            </label>
            <input
              type="text"
              value={bodySerial}
              onChange={(e) => setBodySerial(e.target.value)}
              placeholder="361AB-XXXX"
              className={inputClass}
            />
          </div>

          <p className="text-xs text-gray-500">{t('register.detailHint')}</p>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!serialValid || !hasIdentifier || mutation.isPending}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending
              ? t('register.submitting')
              : t('register.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
